import { useEffect, useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CHAT_BOT } from '__queries/query-keys';
import { ChatBot } from 'ts-components/ChatBot';
import Loader from '__components/Kizen/Loader';
import { CompleteSessionForm } from './components/CompleteSessionForm';
import { SignOutMessage } from './components/SignOutMessage';
import {
  DUPLICATE_RESPONSE_CODE,
  COMPLETED_STATUS_CODE,
  MAX_RETRY_MS,
  RETRY_INTERVAL,
  SAMPLE_IDEAS,
} from './constants';
import { deferExecution } from 'utility/defer';
import debounce from 'lodash/debounce';
import { KZN_FRAME_EVENTS } from './events';
import { Container, PageWrapper } from './styles';
import { QuickResponseData } from 'ts-components/QuickResponses';

const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITEKEY;
const baseURl = import.meta.env.VITE_API_BASE_PATH;

const waitRenderValid = async () => {
  return new Promise((resolve) => {
    let count = 0;
    const check = () => {
      if (
        window &&
        window.grecaptcha &&
        typeof window.grecaptcha.render === 'function'
      ) {
        resolve(true);
      } else if (count < MAX_RETRY_MS) {
        setTimeout(check, RETRY_INTERVAL);
        count += RETRY_INTERVAL;
      } else {
        resolve(false);
      }
    };

    check();
  });
};

const getHasSessionCookie = () =>
  document.cookie
    .split(';')
    .some((cookie) => cookie.trim().startsWith('session_id='));

const BusinessBuilderPublic = () => {
  const location = useLocation();
  const { t } = useTranslation();

  const queryParams = new URLSearchParams(location.search);
  const source = queryParams.get('utm_source') || undefined;
  const campaign = queryParams.get('utm_campaign') || undefined;
  const medium = queryParams.get('utm_medium') || undefined;
  const term = queryParams.get('utm_term') || undefined;
  const content = queryParams.get('utm_content') || undefined;
  const disableHotJar = queryParams.get('disable-hotjar') || undefined;
  const headerLogoUrl = queryParams.get('header-logo-url') || undefined;
  const headerText = queryParams.get('header') || undefined;

  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [completedSessionId, setCompletedSessionId] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaId, setRecaptchaId] = useState<string | null>(null);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [chatErrorMessage, setChatErrorMessage] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [shouldRefreshChatSession, setShouldRefreshChatSession] =
    useState(false);
  const shouldDisableHotJar = disableHotJar === 'true';

  useEffect(() => {
    if (!shouldDisableHotJar) {
      const script = document.createElement('script');
      script.src = '/content-square.js';
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, [shouldDisableHotJar]);

  const trackEvent = useCallback((event: string, payload?: any) => {
    const isInFrame = window.self !== window.top;
    const eventName = `kzn_${event}`;
    if (isInFrame) {
      window.parent.postMessage(
        {
          name: eventName,
          payload,
        },
        '*'
      );
    }
  }, []);

  useEffect(() => {
    const fn = debounce(() => {
      trackEvent(KZN_FRAME_EVENTS.RESIZE, {
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }, 250);

    const observer = new ResizeObserver(fn);

    observer.observe(document.body);

    return () => {
      observer.disconnect();
    };
  }, [trackEvent]);

  const fetchAuth = async () => {
    try {
      const results = await fetch(`${baseURl}/auth/bootstrap`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const data = await results.json();
      return data;
    } catch (e) {
      return {};
    }
  };

  const {
    data: bootstrapData,
    isLoading: sessionValidationLoading,
    refetch: refetchBoostrap,
  } = useQuery({
    queryKey: CHAT_BOT.PUBLIC_BOOTSTRAP,
    queryFn: fetchAuth,
    throwOnError: false,
    retry: false,
  });

  useEffect(() => {
    // Load the reCAPTCHA script
    if (sessionValidationLoading) return;
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [sessionValidationLoading]);

  const execute = useCallback(async () => {
    if (recaptchaId === null) {
      if ((await waitRenderValid()) && window.grecaptcha) {
        const rId = window.grecaptcha.render('recaptcha-container', {
          sitekey: recaptchaSiteKey,
          callback: (token: string) => {
            trackEvent(KZN_FRAME_EVENTS.CAPTCHA_SUCCESS);
            setRecaptchaToken(token);
          },
        });
        setRecaptchaId(rId);
        window.grecaptcha.execute(rId);
      }
    } else {
      window.grecaptcha?.execute(recaptchaId);
    }
  }, [recaptchaId, trackEvent]);

  useEffect(() => {
    if (recaptchaToken !== '' || getHasSessionCookie()) {
      setShouldRefreshChatSession(true);
    }
  }, [recaptchaToken]);

  const resetCaptcha = useCallback(async () => {
    window.location.reload();
  }, []);

  const onClickLogout = useCallback(async () => {
    await fetch(`${baseURl}/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    refetchBoostrap();
  }, [refetchBoostrap]);

  const openKizen = useCallback(() => {
    window.open('/', '_blank');
  }, []);

  const handleDeleteSession = useCallback(async () => {
    await fetch(`${baseURl}/public/businessbuilder/cancel/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    deferExecution(() => resetCaptcha());
  }, [resetCaptcha]);

  const handleFetchSession = useCallback(async () => {
    const options: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    };
    if (!getHasSessionCookie()) {
      options.body = JSON.stringify({
        captcha_value: recaptchaToken,
      });
    }
    const response = await fetch(
      `${baseURl}/public/businessbuilder/session/`,
      options
    );
    if (response.status === 429) {
      setChatErrorMessage(t('Please try again later.'));
    } else {
      setChatErrorMessage('');
    }

    const data = await response.json();
    if (data.code === 'invalid_captcha') {
      resetCaptcha();
    }
    return data;
  }, [recaptchaToken, resetCaptcha, setChatErrorMessage, t]);

  const handleSubmitForm = useCallback(
    async (businessMetadata: {
      first_name: string;
      last_name: string;
      email: string;
      company_name: string;
    }) => {
      setSubmissionError('');
      setSubmissionLoading(true);

      const data = await fetch(
        `${baseURl}/public/businessbuilder/${completedSessionId}/complete/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...businessMetadata,
            captcha_value: recaptchaToken,
            lead_source: {
              source,
              campaign,
              medium,
              term,
              content,
            },
          }),
          credentials: 'include',
        }
      );

      const response = await data.json();

      trackEvent(KZN_FRAME_EVENTS.FORM_SUBMITTED, response);
      setSubmissionLoading(false);

      if (response.status === COMPLETED_STATUS_CODE) {
        setIsCompleted(true);
        setSubmissionError('');
      } else if (response && response.code === DUPLICATE_RESPONSE_CODE) {
        return DUPLICATE_RESPONSE_CODE;
      } else {
        return t('Something went wrong. Please try again.');
      }
    },
    [
      recaptchaToken,
      completedSessionId,
      t,
      trackEvent,
      source,
      campaign,
      medium,
      term,
      content,
    ]
  );

  const [pendingMessageToSend, setPendingMessageToSend] = useState('');
  const mockSession = useMemo(() => {
    if ((!recaptchaToken && !getHasSessionCookie()) || pendingMessageToSend) {
      return [
        {
          id: 'mocked-message',
          isCollapsible: false,
          isResponseLoading: false,
          isUserMessage: false,
          messageContent:
            'Hello! I’m Kizen CoBuilder, specialized in building business applications for various industries. Could you please provide some initial information about your business and what you’re looking to achieve with such a system?',
        },
        pendingMessageToSend
          ? {
              id: 'pending-message',
              isCollapsible: false,
              isResponseLoading: false,
              isUserMessage: true,
              messageContent: pendingMessageToSend,
            }
          : undefined,
        pendingMessageToSend
          ? {
              id: 'loading',
              messageContent: '',
              isUserMessage: false,
              isResponseLoading: true,
              isCollapsable: false,
            }
          : undefined,
      ].filter(Boolean);
    }

    return undefined;
  }, [recaptchaToken, pendingMessageToSend]);

  const sampleIdeas = useMemo(() => {
    const ideas = SAMPLE_IDEAS(t);
    return {
      options: ideas.sort(() => 0.5 - Math.random()).slice(0, 4),
      selectType: 'single_select',
    } as QuickResponseData;
  }, [t]);

  return (
    <PageWrapper>
      {sessionValidationLoading ? (
        <Loader loading />
      ) : bootstrapData.access ? (
        <SignOutMessage onLogout={onClickLogout} openKizen={openKizen} />
      ) : showCompleteForm ? (
        <CompleteSessionForm
          onConfirm={handleSubmitForm}
          error={submissionError}
          loading={submissionLoading}
          clearError={() => setSubmissionError('')}
          isCompleted={isCompleted}
          trackEvent={trackEvent}
        />
      ) : (
        <ChatBot
          showLogo={!!headerLogoUrl}
          headerLogoUrl={headerLogoUrl}
          header={headerText}
          entityName={t('App')}
          trackEvent={trackEvent}
          fetchSession={handleFetchSession}
          onCompleteSession={(id) => {
            setCompletedSessionId(id);
            setShowCompleteForm(true);
          }}
          getStreamingEndpoint={(id) =>
            `${baseURl}/public/businessbuilder/${id}/send-message-streaming/`
          }
          onDeleteSession={handleDeleteSession}
          errorMessage={chatErrorMessage ? chatErrorMessage : undefined}
          stopRefetchAfterDelete
          forceSessionRefetch={shouldRefreshChatSession}
          disableSessionRefetch={() => setShouldRefreshChatSession(false)}
          eventListenerNames={[
            'business_usecase',
            'app_requirements',
            'strategic_plan',
          ]}
          triggerResend={Boolean(recaptchaToken)}
          mockSession={mockSession}
          onMockedSessionInteraction={async (messageToSend) => {
            setPendingMessageToSend(messageToSend);

            if (messageToSend) {
              await execute();
            }
          }}
          renderMarkdown
          sampleIdeas={sampleIdeas}
        />
      )}
      <Container
        id="recaptcha-container"
        className="g-recaptcha"
        data-sitekey={recaptchaSiteKey}
        data-size="invisible"
      ></Container>
    </PageWrapper>
  );
};

export default BusinessBuilderPublic;
